/**
 * Entra ID sign-in for the browser (§8.1).
 *
 * Auth-code flow with PKCE, which is why there is no client secret anywhere in this
 * repository and never should be: a SPA cannot keep one.
 *
 * The token this obtains is an **access token for the API**, not an ID token. The API
 * validates `aud` against its own App ID URI, and an ID token's audience is the bare
 * client id — so an ID token authenticates the user to the browser and is refused by the
 * server, which is a confusing half-hour if you have not met it before.
 */

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import type { AccountInfo, AuthenticationResult } from '@azure/msal-browser';

import { configValue } from '../runtime-config.js';

const env = import.meta.env;

/**
 * Every value here is resolved at run time so one image can serve every environment —
 * a tenant id baked into the bundle at `docker build` cannot be promoted (see
 * `runtime-config.ts`). The `import.meta.env` argument is the local-development fallback.
 */
const tenantId = configValue('VITE_ENTRA_TENANT_ID', env['VITE_ENTRA_TENANT_ID']);
const clientId = configValue('VITE_ENTRA_CLIENT_ID', env['VITE_ENTRA_CLIENT_ID']);

/** True when the local bypass is on and MSAL should not be initialised at all. */
export const devBypass =
  configValue('VITE_AUTH_DEV_BYPASS', env['VITE_AUTH_DEV_BYPASS']) === 'true';

export const entraConfigured = tenantId !== undefined && clientId !== undefined;

/**
 * The scope to request for the API.
 *
 * **The resource is the bare GUID, not `api://<guid>`.** The SPA and the API are one app
 * registration here, so the app is requesting a token for itself, and Entra supports that
 * only when the resource is given as the GUID-based App Identifier:
 *
 *     AADSTS500207: Application '<guid>'(api://<guid>) is requesting a token for itself.
 *     This scenario is supported only if resource is specified using the GUID based App
 *     Identifier.
 *
 * The GUID form is also valid when the API is a *separate* registration, so this is the
 * safer default either way — override with `VITE_ENTRA_API_SCOPE` when the resource is a
 * different app, or to name a specific scope (`<guid>/access_as_user`).
 *
 * A consequence worth knowing: the resulting token's `aud` is the bare client id rather
 * than the App ID URI, which is why the API accepts both forms.
 */
export const apiScope =
  configValue('VITE_ENTRA_API_SCOPE', env['VITE_ENTRA_API_SCOPE']) ??
  (clientId === undefined ? '' : `${clientId}/.default`);

export const msal =
  entraConfigured && !devBypass
    ? new PublicClientApplication({
        auth: {
          clientId: clientId as string,
          authority: `https://login.microsoftonline.com/${tenantId as string}`,
          // Must match a redirect URI registered as type "SPA" on the app registration.
          redirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
        },
        cache: {
          // Session storage, not local: a shared machine should not keep a compliance
          // auditor signed in after the tab closes. It also means the token is gone on
          // browser restart, which is the behaviour §8.1 asks for.
          cacheLocation: 'sessionStorage',
        },
      })
    : null;

let initialised = false;

/** MSAL must be initialised before any other call. Safe to call repeatedly. */
export async function initialiseMsal(): Promise<void> {
  if (msal === null || initialised) return;
  await msal.initialize();
  // Completes a redirect sign-in if we have just come back from one.
  await msal.handleRedirectPromise();
  initialised = true;
}

export function currentAccount(): AccountInfo | null {
  if (msal === null) return null;
  return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
}

/**
 * A token for the API, silently if possible.
 *
 * Returns null when nobody is signed in — the caller shows the sign-in screen rather than
 * throwing, because "not signed in yet" is a normal state on first load, not an error.
 */
export async function acquireApiToken(): Promise<string | null> {
  if (msal === null) return null;
  await initialiseMsal();

  const account = currentAccount();
  if (account === null) return null;

  try {
    const result: AuthenticationResult = await msal.acquireTokenSilent({
      account,
      scopes: [apiScope],
    });
    return result.accessToken;
  } catch (error) {
    // A silent failure that needs a human is the one case where redirecting is correct;
    // anything else is a real error and must not turn into an infinite redirect loop.
    if (error instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ account, scopes: [apiScope] });
      return null;
    }
    throw error;
  }
}

export async function signIn(): Promise<void> {
  if (msal === null) return;
  await initialiseMsal();
  await msal.loginRedirect({
    scopes: [apiScope],
    // Forces the account picker rather than silently reusing a session, which matters on
    // a machine shared between an auditor and a delivery manager.
    prompt: 'select_account',
  });
}

export async function signOut(): Promise<void> {
  if (msal === null) return;
  await initialiseMsal();
  const account = currentAccount();
  await msal.logoutRedirect(account === null ? {} : { account });
}
